import Decimal from 'decimal.js-light';
import { formatUnits } from 'viem';

import { tokenFormatter } from '@/lib/formatting';

import type { TokenUsdPrices } from '@/components/TransactionModule/tradeQuote';

import { solana } from '@/constants/chain';

import { isSameTokenAddress } from '@/utils/normalizeAddress';
import { canonicalSolanaMint } from '@/utils/token-category';

import type { Fee, Fees } from './0x.types';
import type { TokenResult } from './0x/token-registry.types';

export type TradeFlow = 'swap' | 'gasless' | 'intents';

export type FeeLineItem = {
  kind: 'gasRebate' | 'matcha' | 'matchaAuto' | 'integrator';
  label: string;
  formattedAmount?: string;
  token?: TokenResult;
};

export const isFeeInToken = (
  feeTokenAddress: string | undefined,
  token: Pick<TokenResult, 'address' | 'chainId'>,
): boolean => {
  const normalizedFeeTokenAddress =
    token.chainId === solana.id && feeTokenAddress
      ? canonicalSolanaMint(feeTokenAddress)
      : feeTokenAddress;
  const normalizedTokenAddress =
    token.chainId === solana.id ? canonicalSolanaMint(token.address) : token.address;

  return isSameTokenAddress(normalizedFeeTokenAddress, normalizedTokenAddress);
};

const resolveFeeToken = (fee: Fee, sellToken: TokenResult, buyToken: TokenResult): TokenResult =>
  isFeeInToken(fee.token, sellToken) ? sellToken : buyToken;

const formatFeeAmount = (fee: Fee, token: TokenResult): string =>
  tokenFormatter.format(formatUnits(BigInt(fee.amount), Number(token.decimals)), {
    precision: 3,
    symbol: token.symbol,
  });

const deriveVolumeFeePercent = (
  fee: Fee,
  sellToken: TokenResult,
  sellAmount: string | undefined,
  buyAmount: string | undefined,
): number | undefined => {
  if (fee.type !== 'volume') return undefined;

  const volumeRaw = isFeeInToken(fee.token, sellToken) ? sellAmount : buyAmount;
  if (!volumeRaw) return undefined;

  const volumeAmount = new Decimal(volumeRaw);
  if (volumeAmount.isZero()) return undefined;

  return new Decimal(fee.amount).div(volumeAmount).mul(100).toNumber();
};

const formatFeePercent = (percent: number): string => {
  const rounded = parseFloat(percent.toFixed(2));
  return `${rounded > 0 || percent === 0 ? rounded : percent.toPrecision(1)}%`;
};

/**
 * Normalizes the `fees` object of a 0x price/quote response into display-ready
 * line items. The same `zeroExFee` field means "Matcha Fee" on the standard
 * swap flow and "Matcha Auto Fee" on the gasless flow; `flow` disambiguates.
 * Percentages are derived from the response amounts where possible;
 * `fallbackMatchaAutoFeePercent` covers gasless fees that cannot be derived.
 */
export const parseFees = ({
  fees,
  flow,
  sellToken,
  buyToken,
  sellAmount,
  buyAmount,
  swapFeeBps,
  integratorName,
  fallbackMatchaAutoFeePercent,
}: {
  fees: Fees | undefined;
  flow: TradeFlow;
  sellToken: TokenResult;
  buyToken: TokenResult;
  sellAmount?: string;
  buyAmount?: string;
  swapFeeBps?: number;
  integratorName?: string;
  fallbackMatchaAutoFeePercent?: number;
}): FeeLineItem[] => {
  const items: FeeLineItem[] = [];

  // 'gasless' and 'intents' are both the Matcha Auto flow; fee display is identical.
  const isMatchaAuto = flow === 'gasless' || flow === 'intents';

  if (isMatchaAuto && fees?.gasFee) {
    const token = resolveFeeToken(fees.gasFee, sellToken, buyToken);
    items.push({
      kind: 'gasRebate',
      label: 'Network Cost Rebate',
      formattedAmount: formatFeeAmount(fees.gasFee, token),
      token,
    });
  }

  const zeroExFee = fees?.zeroExFee;
  if (isMatchaAuto) {
    if (!zeroExFee) {
      items.push({ kind: 'matchaAuto', label: 'Matcha Auto Fee (0%)' });
    } else {
      const token = resolveFeeToken(zeroExFee, sellToken, buyToken);
      const percent =
        deriveVolumeFeePercent(zeroExFee, sellToken, sellAmount, buyAmount) ??
        fallbackMatchaAutoFeePercent;
      items.push({
        kind: 'matchaAuto',
        label: `Matcha Auto Fee${percent !== undefined ? ` (${formatFeePercent(percent)})` : ''}`,
        formattedAmount: formatFeeAmount(zeroExFee, token),
        token,
      });
    }
  } else if (zeroExFee) {
    const token = resolveFeeToken(zeroExFee, sellToken, buyToken);
    const percent = deriveVolumeFeePercent(zeroExFee, sellToken, sellAmount, buyAmount);
    items.push({
      kind: 'matcha',
      label: `Matcha Fee${percent !== undefined ? ` (${formatFeePercent(percent)})` : ''}`,
      formattedAmount: formatFeeAmount(zeroExFee, token),
      token,
    });
  }

  if (swapFeeBps && integratorName) {
    const integratorFee = fees?.integratorFee;
    const token = integratorFee ? resolveFeeToken(integratorFee, sellToken, buyToken) : undefined;
    items.push({
      kind: 'integrator',
      label: `${integratorName} Fee (${swapFeeBps / 100}%)`,
      formattedAmount: integratorFee && token ? formatFeeAmount(integratorFee, token) : undefined,
      token,
    });
  }

  return items;
};

export const calculateZeroExFeeInUsd = (
  zeroExFee: Fee | null | undefined,
  sellToken: Pick<TokenResult, 'address' | 'chainId' | 'decimals'>,
  buyToken: Pick<TokenResult, 'address' | 'chainId' | 'decimals'>,
  tokenUsdPrices: TokenUsdPrices,
): number => {
  if (!zeroExFee) return 0;

  const isSellToken = isFeeInToken(zeroExFee.token, sellToken);
  const decimals = Number(isSellToken ? sellToken.decimals : buyToken.decimals);
  if (!decimals) return 0;

  const usdPrice = isSellToken ? tokenUsdPrices.sellTokenUsd : tokenUsdPrices.buyTokenUsd;
  return parseFloat(formatUnits(BigInt(zeroExFee.amount), decimals)) * usdPrice;
};
